
import { useState, useEffect, useRef } from 'react';
import { BlogPost } from '@/types/blog';
import { UsePostBySlugReturn } from './types';
import { fetchBlogPostBySlug } from './utils/fetchUtils';
import { isSSR } from '@/lib/isSSR';
import { getSinglePostFromServerCache } from '@/services/blog/cache';

/**
 * Enhanced version of usePostBySlug that checks for server-side rendered data first
 * This ensures that the blog post content is fully rendered on the server side
 * Fixed to properly handle cache invalidation when navigating between related posts
 */
export const useHydratedPostBySlug = (
  slug: string, 
  preloadedPost: BlogPost | null = null
): UsePostBySlugReturn => {
  // Check if we're in SSR mode
  const ssr = isSSR();
  
  // Keep track of the previous slug to detect changes
  const prevSlugRef = useRef<string | null>(null);
  const hasSlugChanged = prevSlugRef.current !== null && prevSlugRef.current !== slug;
  
  // First try to use the directly passed preloaded post
  // Then try to get the post from server cache if we're in SSR mode
  const cachedData = preloadedPost 
    ? { data: preloadedPost, isValid: true }
    : (ssr ? getSinglePostFromServerCache(slug) : { data: null, isValid: false });

  const initialPost = preloadedPost || (cachedData.isValid ? cachedData.data as BlogPost : null);
  
  // Initialize state with preloaded/cached data if available
  const [post, setPost] = useState<BlogPost | null>(initialPost || null);
  const [loading, setLoading] = useState(!initialPost);
  const [error, setError] = useState<string | null>(null);

  // Clear cache and reset state when slug changes (for related posts navigation)
  useEffect(() => {
    if (hasSlugChanged) {
      console.log(`[Blog] Slug changed from ${prevSlugRef.current} to ${slug}, clearing cache and resetting state`);
      
      // Clear the current post immediately to prevent showing wrong content
      setPost(null);
      setLoading(true);
      setError(null);
      
      // Clear window cache for the old slug to prevent conflicts
      if (!ssr && typeof window !== 'undefined' && window.__BLOG_POST_DATA__ && prevSlugRef.current) {
        delete window.__BLOG_POST_DATA__[prevSlugRef.current];
        console.log(`[Blog] Cleared window cache for old slug: ${prevSlugRef.current}`);
      }
    }
    
    // Update the previous slug reference
    prevSlugRef.current = slug;
  }, [slug, hasSlugChanged, ssr]);

  // Try to get blog post data from window object (for client-side hydration)
  useEffect(() => {
    // If we already have the post from preload or SSR and slug hasn't changed, don't fetch again
    if (initialPost && !hasSlugChanged) {
      console.log(`[Blog] Using preloaded/server-rendered post data for slug: ${slug}`);
      setPost(initialPost);
      setLoading(false);
      return;
    }
    
    // Check for window data before fetching (only if slug hasn't changed or we don't have cached data)
    if (!ssr && typeof window !== 'undefined' && !hasSlugChanged) {
      // Check if window.__BLOG_POST_DATA__ exists
      if (window.__BLOG_POST_DATA__) {
        const windowData = window.__BLOG_POST_DATA__[slug];
        if (windowData && windowData.slug === slug) { // Ensure the cached data matches the current slug
          console.log(`[Blog] Using hydrated post data from window for slug: ${slug}`);
          setPost(windowData as BlogPost);
          setLoading(false);
          return;
        }
      }

      // Also check for script tag with JSON data
      const scriptElement = document.getElementById('__BLOG_POST_DATA__');
      if (scriptElement && scriptElement.textContent) {
        try {
          const jsonData = JSON.parse(scriptElement.textContent);
          if (jsonData[slug] && jsonData[slug].slug === slug) { // Ensure the cached data matches the current slug
            console.log(`[Blog] Using script tag data for slug: ${slug}`);
            setPost(jsonData[slug] as BlogPost);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.error('[Blog] Error parsing blog post data from script tag:', e);
        }
      }
    }
    
    const loadPost = async () => {
      if (!slug) return;
      
      try {
        setLoading(true);
        setError(null);
        
        console.log(`[Blog] Fetching blog post for slug: ${slug}`);
        const postData = await fetchBlogPostBySlug(slug);
        
        if (postData) {
          console.log(`[Blog] Successfully fetched blog post: ${postData.title}`);
          setPost(postData);
          
          // Cache the fetched data in window object for future use
          if (!ssr && typeof window !== 'undefined') {
            if (typeof window.__BLOG_POST_DATA__ === 'undefined') {
              window.__BLOG_POST_DATA__ = {};
            }
            window.__BLOG_POST_DATA__[slug] = postData;
            console.log(`[Blog] Cached post data in window for slug: ${slug}`);
          }
        } else {
          console.log(`[Blog] No post found for slug: ${slug}`);
          setPost(null);
        }
      } catch (err) {
        console.error(`[Blog] Error fetching post: ${err}`);
        setError('Errore nel caricamento del post');
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug, initialPost, ssr, hasSlugChanged]);

  return { post, loading, error };
};
